/**
 * RWBR — fictional driver creation, inaugural season.
 *
 * SOURCE: Mike Sagliano (novaria08), *A Fictional Guide to Red White & Blue
 * Racin'*, Version 2, PART ONE, pp. 1-4. Every table below is transcribed
 * verbatim from that document; the page cite travels with the table so a
 * generated driver can point at the printed row that produced him.
 *
 * Sagliano's framing (p. 1, verbatim):
 *   "Use 2d10 or a random number generator to create 'SCRAM-like' drivers in
 *    the league. Rolling on the Number of Qualities Table: Inaugural Season
 *    should provide a fairly accurate distribution of drivers in the league
 *    when it is first formed."
 * And the attribution (p. 2, verbatim):
 *   "All of the driver creation tables are based on my analysis of the
 *    distribution of qualities, symbols, etc. in the set of SCRAM cards..."
 *
 * THE CHAIN IS THE RULE. This is not a bag of independent rolls — each result
 * selects the table the next roll is made on:
 *
 *   # of qualities ──┬─> which qualities (roll as often as needed)
 *                    ├─> # of ★  ──> which quality is ★
 *                    ├─> GAS PEDALS   (table chosen by quality count)
 *                    ├─> BRAKE PEDALS (table chosen by quality count)
 *                    ├─> TROPHY       (table chosen by quality count)
 *                    │     └─> PIT CREW A-E (table chosen by trophy)
 *                    │            └─> FAST PIT 2-12 (table chosen by grade)
 *                    └─> ESCAPE 2-11  (table chosen by quality count)
 *
 * Generate in that order and nothing else. A one-quality driver can only ever
 * hold NONE for a trophy, which forces him onto the NONE pit-crew row, which
 * caps his fast pit — the correlations on a real card are produced by the
 * chain, not by any single table.
 *
 * RULINGS (each also marked inline at the point it bites):
 *   R-1  2d10 is read as a PERCENTILE 1-100, not a 2-20 sum.
 *   R-2  A ★ roll that lands on a quality the driver does not hold is discarded
 *        and re-rolled.
 *   R-3  The number of ★ is capped at the number of qualities held.
 *   R-4  The same quality is never starred twice.
 *   R-5  The Escape row for two-quality drivers reads 47-67 (printed "47--67").
 *   R-6  `triangle` is false — Sagliano publishes no symbol table.
 *   R-7  `fastPitDouble` is null — the split grade is an EZRA add-on, not here.
 *   R-8  `age` is not set. Sagliano's model has no age field (D-002 adds one as
 *        an explicit house layer, elsewhere).
 *
 * PURITY: the seeded `Dice` is the only stateful argument; nothing is mutated.
 */

import type { DriverCard, PitCrewGrade, Quality, Trophy } from './types.ts';
import { QUALITIES } from './types.ts';
import type { Dice } from './dice.ts';

export const CITE = "Sagliano, A Fictional Guide to Red White & Blue Racin', v2, Part One";

/** Sagliano's tables are all keyed on a quality count of 1-5. */
export type QualityCount = 1 | 2 | 3 | 4 | 5;
export const QUALITY_COUNTS: readonly QualityCount[] = [1, 2, 3, 4, 5];

/** The four rows of the Pit Crew table. `none` is a real value, not "unrated". */
export type TrophyKey = 'none' | 'bronze' | 'silver' | 'gold';
export const TROPHY_KEYS: readonly TrophyKey[] = ['none', 'bronze', 'silver', 'gold'];
export const PIT_CREW_GRADES: readonly PitCrewGrade[] = ['A', 'B', 'C', 'D', 'E'];

/** A trophy value as it is stored on the card: `null` means "no trophy". */
export function trophyKeyOf(trophy: Trophy): TrophyKey {
  return trophy === null ? 'none' : trophy;
}

// ---------------------------------------------------------------------------
// The die
// ---------------------------------------------------------------------------

export interface PercentileRoll {
  /** 1-100. */
  value: number;
  /** The two d10 faces as thrown, tens die first. A 10 reads as 0. */
  dice: [number, number];
}

/**
 * RULING R-1 — 2d10 is a PERCENTILE read, not a sum.
 *
 * Every inaugural table spans 1-100 and none has a row below 1 or above 100; a
 * 2d10 SUM could only produce 2-20. Sagliano's own alternative ("or a random
 * number generator") confirms it: the tables want a flat 1-100. Tens die first,
 * a 10 on either die reads as 0, and 00 is 100 — the standard d100 read.
 *
 * `Dice` has no percentile helper and dice.ts is frozen, so it is built here
 * from two `d10()` calls. That also keeps the "notations do not mix" discipline
 * dice.ts sets out: this is the only place in the engine that reads 1-100.
 */
export function rollPercentile(dice: Dice): PercentileRoll {
  const tensFace = dice.d10();
  const onesFace = dice.d10();
  const value = (tensFace % 10) * 10 + (onesFace % 10);
  return { value: value === 0 ? 100 : value, dice: [tensFace, onesFace] };
}

// ---------------------------------------------------------------------------
// Table shape
// ---------------------------------------------------------------------------

export interface RollBand<T> {
  /** Inclusive, 1-100. */
  min: number;
  /** Inclusive, 1-100. */
  max: number;
  value: T;
  note?: string;
}

export interface RollTable<T> {
  /** Stable identifier, recorded in provenance so the chain is inspectable. */
  id: string;
  /** The table's printed name, verbatim. */
  name: string;
  /** Page cite in the guide. */
  page: string;
  /** What selected this table, when it is one row of a conditional set. */
  conditionedOn?: string;
  bands: RollBand<T>[];
}

/** The band a 1-100 roll lands in. Throws if the table has a hole in it. */
export function lookup<T>(table: RollTable<T>, roll: number): RollBand<T> {
  if (!Number.isInteger(roll) || roll < 1 || roll > 100) {
    throw new RangeError(`${table.name}: roll must be an integer 1-100, got ${roll}`);
  }
  const band = table.bands.find((b) => roll >= b.min && roll <= b.max);
  if (!band) {
    throw new Error(
      `${table.name} (${table.page}): no band covers a roll of ${roll}. The transcription has a ` +
        `gap — fix the table against the source, do not special-case the caller.`,
    );
  }
  return band;
}

/** "59-85", for the audit trail. */
export function bandLabel<T>(band: RollBand<T>): string {
  return band.min === band.max ? `${band.min}` : `${band.min}-${band.max}`;
}

// ---------------------------------------------------------------------------
// THE TABLES — transcribed verbatim, p. 1-4
// ---------------------------------------------------------------------------

/**
 * p. 1 — "Creating Drivers: Qualities", Number of Qualities Table: Inaugural Season.
 * "First determine how many qualities each driver will have."
 */
export const NUMBER_OF_QUALITIES: RollTable<QualityCount> = {
  id: 'number-of-qualities',
  name: 'Number of Qualities Table: Inaugural Season',
  page: 'p. 1',
  bands: [
    { min: 1, max: 12, value: 1 },
    { min: 13, max: 58, value: 2 },
    { min: 59, max: 85, value: 3 },
    { min: 86, max: 97, value: 4 },
    { min: 98, max: 100, value: 5 },
  ],
};

/**
 * p. 1 — Quality by Driver Table.
 * "Roll as often as needed on the Quality by Driver Table to determine which
 *  qualities the driver has. Ignore the same quality rolled repeatedly."
 */
export const QUALITY_BY_DRIVER: RollTable<Quality> = {
  id: 'quality-by-driver',
  name: 'Quality by Driver Table',
  page: 'p. 1',
  bands: [
    { min: 1, max: 19, value: 'REFLEX' },
    { min: 20, max: 41, value: 'CONTROL' },
    { min: 42, max: 62, value: 'SKILL' },
    { min: 63, max: 75, value: 'STAR' },
    { min: 76, max: 87, value: 'SMART' },
    { min: 88, max: 100, value: 'HEAT' },
  ],
};

/**
 * p. 2 — Number of ★ Table.
 * "First, roll on the Number of ★ Table to determine the number of qualities
 *  starred ★."
 */
export const NUMBER_OF_STARS: RollTable<0 | 1 | 2> = {
  id: 'number-of-stars',
  name: 'Number of ★ Table',
  page: 'p. 2',
  bands: [
    { min: 1, max: 79, value: 0 },
    { min: 80, max: 97, value: 1 },
    { min: 98, max: 100, value: 2 },
  ],
};

/**
 * p. 2 — ★ Quality of Driver Table.
 * "Second, roll on the ★ Quality of Driver Table to determine which quality is
 *  ★ starred."
 *
 * Note the weights differ from the unstarred table — STAR is 24 points wide
 * here against 13 there. Transcribed as printed.
 */
export const STAR_QUALITY_OF_DRIVER: RollTable<Quality> = {
  id: 'star-quality-of-driver',
  name: '★ Quality of Driver Table',
  page: 'p. 2',
  bands: [
    { min: 1, max: 18, value: 'REFLEX' },
    { min: 19, max: 36, value: 'CONTROL' },
    { min: 37, max: 54, value: 'SKILL' },
    { min: 55, max: 78, value: 'STAR' },
    { min: 79, max: 95, value: 'SMART' },
    { min: 96, max: 100, value: 'HEAT' },
  ],
};

/**
 * p. 2 — "Creating Drivers: Gas Pedals".
 * "Gas Pedals ■ depend on the # of Qualities. Roll 2d10 on table below to
 *  determine the number of Gas Pedals."
 *
 * One table per quality count. A five-quality driver is always 5 gas.
 */
export const GAS_PEDALS: Record<QualityCount, RollTable<number>> = {
  1: {
    id: 'gas-pedals-q1',
    name: 'Creating Drivers: Gas Pedals',
    page: 'p. 2',
    conditionedOn: '# of Qualities = 1',
    bands: [
      { min: 1, max: 71, value: 2 },
      { min: 72, max: 100, value: 3 },
    ],
  },
  2: {
    id: 'gas-pedals-q2',
    name: 'Creating Drivers: Gas Pedals',
    page: 'p. 2',
    conditionedOn: '# of Qualities = 2',
    bands: [
      { min: 1, max: 22, value: 2 },
      { min: 23, max: 78, value: 3 },
      { min: 79, max: 100, value: 4 },
    ],
  },
  3: {
    id: 'gas-pedals-q3',
    name: 'Creating Drivers: Gas Pedals',
    page: 'p. 2',
    conditionedOn: '# of Qualities = 3',
    bands: [
      { min: 1, max: 6, value: 2 },
      { min: 7, max: 37, value: 3 },
      { min: 38, max: 87, value: 4 },
      { min: 88, max: 100, value: 5 },
    ],
  },
  4: {
    id: 'gas-pedals-q4',
    name: 'Creating Drivers: Gas Pedals',
    page: 'p. 2',
    conditionedOn: '# of Qualities = 4',
    bands: [
      { min: 1, max: 63, value: 4 },
      { min: 64, max: 100, value: 5 },
    ],
  },
  5: {
    id: 'gas-pedals-q5',
    name: 'Creating Drivers: Gas Pedals',
    page: 'p. 2',
    conditionedOn: '# of Qualities = 5',
    bands: [{ min: 1, max: 100, value: 5 }],
  },
};

/**
 * p. 3 — "Creating Drivers: Brake Pedals".
 * "Brake Pedals * depend on the # of Qualities. Roll 2d10 on table below to
 *  determine the number of Brake Pedals."
 *
 * The five-quality row has no 0 column: those drivers always carry a brake.
 * Three brakes exist on the four-quality row only.
 */
export const BRAKE_PEDALS: Record<QualityCount, RollTable<number>> = {
  1: {
    id: 'brake-pedals-q1',
    name: 'Creating Drivers: Brake Pedals',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 1',
    bands: [
      { min: 1, max: 71, value: 0 },
      { min: 72, max: 100, value: 1 },
    ],
  },
  2: {
    id: 'brake-pedals-q2',
    name: 'Creating Drivers: Brake Pedals',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 2',
    bands: [
      { min: 1, max: 68, value: 0 },
      { min: 69, max: 93, value: 1 },
      { min: 94, max: 100, value: 2 },
    ],
  },
  3: {
    id: 'brake-pedals-q3',
    name: 'Creating Drivers: Brake Pedals',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 3',
    bands: [
      { min: 1, max: 19, value: 0 },
      { min: 20, max: 63, value: 1 },
      { min: 64, max: 100, value: 2 },
    ],
  },
  4: {
    id: 'brake-pedals-q4',
    name: 'Creating Drivers: Brake Pedals',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 4',
    bands: [
      { min: 1, max: 28, value: 0 },
      { min: 29, max: 42, value: 1 },
      { min: 43, max: 71, value: 2 },
      { min: 72, max: 100, value: 3 },
    ],
  },
  5: {
    id: 'brake-pedals-q5',
    name: 'Creating Drivers: Brake Pedals',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 5',
    bands: [
      { min: 1, max: 50, value: 1 },
      { min: 51, max: 100, value: 2 },
    ],
  },
};

/**
 * p. 3 — "Creating Drivers: Trophies or None".
 * "Trophy (or None) depend on the # of Qualities. Roll 2d10 on table below."
 *
 * A one-quality driver can never hold a trophy, and the five-quality row prints
 * no SILVER band at all — gold or bronze, nothing between. Both are transcribed
 * exactly as published; neither is smoothed.
 */
export const TROPHY_TABLE: Record<QualityCount, RollTable<Trophy>> = {
  1: {
    id: 'trophy-q1',
    name: 'Creating Drivers: Trophies or None',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 1',
    bands: [{ min: 1, max: 100, value: null, note: 'NONE — a one-quality driver never carries a trophy' }],
  },
  2: {
    id: 'trophy-q2',
    name: 'Creating Drivers: Trophies or None',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 2',
    bands: [
      { min: 1, max: 7, value: 'gold' },
      { min: 8, max: 36, value: 'silver' },
      { min: 37, max: 100, value: 'bronze' },
    ],
  },
  3: {
    id: 'trophy-q3',
    name: 'Creating Drivers: Trophies or None',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 3',
    bands: [
      { min: 1, max: 38, value: 'gold' },
      { min: 39, max: 88, value: 'silver' },
      { min: 89, max: 100, value: 'bronze' },
    ],
  },
  4: {
    id: 'trophy-q4',
    name: 'Creating Drivers: Trophies or None',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 4',
    bands: [
      { min: 1, max: 50, value: 'gold' },
      { min: 51, max: 67, value: 'silver' },
      { min: 68, max: 100, value: 'bronze' },
    ],
  },
  5: {
    id: 'trophy-q5',
    name: 'Creating Drivers: Trophies or None',
    page: 'p. 3',
    conditionedOn: '# of Qualities = 5',
    bands: [
      { min: 1, max: 50, value: 'gold' },
      { min: 51, max: 100, value: 'bronze', note: 'the printed row has no SILVER band' },
    ],
  },
};

/**
 * p. 3 — "Creating Drivers: Pit Crews".
 * "Pit Crew depends on Trophies. Roll 2d10 on table below."
 *
 * A gold trophy can only ever draw an A or B crew; NONE can only draw C, D, E.
 */
export const PIT_CREW: Record<TrophyKey, RollTable<PitCrewGrade>> = {
  none: {
    id: 'pit-crew-none',
    name: 'Creating Drivers: Pit Crews',
    page: 'p. 3',
    conditionedOn: 'Trophy = NONE',
    bands: [
      { min: 1, max: 10, value: 'C' },
      { min: 11, max: 65, value: 'D' },
      { min: 66, max: 100, value: 'E' },
    ],
  },
  bronze: {
    id: 'pit-crew-bronze',
    name: 'Creating Drivers: Pit Crews',
    page: 'p. 3',
    conditionedOn: 'Trophy = BRONZE',
    bands: [
      { min: 1, max: 27, value: 'A' },
      { min: 28, max: 40, value: 'B' },
      { min: 41, max: 53, value: 'C' },
      { min: 54, max: 87, value: 'D' },
      { min: 88, max: 100, value: 'E' },
    ],
  },
  silver: {
    id: 'pit-crew-silver',
    name: 'Creating Drivers: Pit Crews',
    page: 'p. 3',
    conditionedOn: 'Trophy = SILVER',
    bands: [
      { min: 1, max: 38, value: 'A' },
      { min: 39, max: 69, value: 'B' },
      { min: 70, max: 100, value: 'C' },
    ],
  },
  gold: {
    id: 'pit-crew-gold',
    name: 'Creating Drivers: Pit Crews',
    page: 'p. 3',
    conditionedOn: 'Trophy = GOLD',
    bands: [
      { min: 1, max: 55, value: 'A' },
      { min: 56, max: 100, value: 'B' },
    ],
  },
};

/**
 * p. 4 — "Creating Drivers: Fast Pit".
 * "Fast Pit depends on the Pit Crew. Roll 2d10 on table below."
 *
 * The printed grid runs 2-12 but no row reaches 11 or 12, and several rows skip
 * values outright (C has no 3; E jumps 4 → 8). Transcribed as printed: the
 * skipped columns are empty cells in the source, not gaps in the roll range.
 */
export const FAST_PIT: Record<PitCrewGrade, RollTable<number>> = {
  A: {
    id: 'fast-pit-a',
    name: 'Creating Drivers: Fast Pit',
    page: 'p. 4',
    conditionedOn: 'Pit Crew = A',
    bands: [
      { min: 1, max: 6, value: 5 },
      { min: 7, max: 12, value: 6 },
      { min: 13, max: 43, value: 7 },
      { min: 44, max: 74, value: 8 },
      { min: 75, max: 87, value: 9 },
      { min: 88, max: 100, value: 10 },
    ],
  },
  B: {
    id: 'fast-pit-b',
    name: 'Creating Drivers: Fast Pit',
    page: 'p. 4',
    conditionedOn: 'Pit Crew = B',
    bands: [
      { min: 1, max: 36, value: 6 },
      { min: 37, max: 100, value: 7 },
    ],
  },
  C: {
    id: 'fast-pit-c',
    name: 'Creating Drivers: Fast Pit',
    page: 'p. 4',
    conditionedOn: 'Pit Crew = C',
    bands: [
      { min: 1, max: 14, value: 2 },
      { min: 15, max: 28, value: 4, note: 'the printed row has no 3 column' },
      { min: 29, max: 86, value: 5 },
      { min: 87, max: 100, value: 6 },
    ],
  },
  D: {
    id: 'fast-pit-d',
    name: 'Creating Drivers: Fast Pit',
    page: 'p. 4',
    conditionedOn: 'Pit Crew = D',
    bands: [
      { min: 1, max: 41, value: 2 },
      { min: 42, max: 94, value: 3 },
      { min: 95, max: 100, value: 4 },
    ],
  },
  E: {
    id: 'fast-pit-e',
    name: 'Creating Drivers: Fast Pit',
    page: 'p. 4',
    conditionedOn: 'Pit Crew = E',
    bands: [
      { min: 1, max: 78, value: 2 },
      { min: 79, max: 89, value: 4 },
      { min: 90, max: 100, value: 8, note: 'printed as-is — E jumps from 4 straight to 8' },
    ],
  },
};

/**
 * p. 4 — "Creating Drivers: Escape".
 * "Escape depends on the # of Qualities. Roll 2d10 on table below."
 *
 * RULING R-5 — the two-quality row's 8 band is printed "47--67" (a doubled
 * hyphen, flagged in the transcription). Read as 47-67: it is the only reading
 * that makes the row a clean partition, closing 46 → 47 and 67 → 68 exactly.
 */
export const ESCAPE: Record<QualityCount, RollTable<number>> = {
  1: {
    id: 'escape-q1',
    name: 'Creating Drivers: Escape',
    page: 'p. 4',
    conditionedOn: '# of Qualities = 1',
    bands: [
      { min: 1, max: 29, value: 4 },
      { min: 30, max: 44, value: 5 },
      { min: 45, max: 59, value: 6 },
      { min: 60, max: 73, value: 7 },
      { min: 74, max: 87, value: 8 },
      { min: 88, max: 100, value: 9 },
    ],
  },
  2: {
    id: 'escape-q2',
    name: 'Creating Drivers: Escape',
    page: 'p. 4',
    conditionedOn: '# of Qualities = 2',
    bands: [
      { min: 1, max: 2, value: 2 },
      { min: 3, max: 7, value: 3 },
      { min: 8, max: 25, value: 4 },
      { min: 26, max: 36, value: 5 },
      { min: 37, max: 43, value: 6 },
      { min: 44, max: 46, value: 7 },
      { min: 47, max: 67, value: 8, note: 'RULING R-5: printed "47--67"' },
      { min: 68, max: 78, value: 9 },
      { min: 79, max: 89, value: 10 },
      { min: 90, max: 100, value: 11 },
    ],
  },
  3: {
    id: 'escape-q3',
    name: 'Creating Drivers: Escape',
    page: 'p. 4',
    conditionedOn: '# of Qualities = 3',
    bands: [
      { min: 1, max: 25, value: 5 },
      { min: 26, max: 56, value: 7, note: 'the printed row has no 6 column' },
      { min: 57, max: 75, value: 8 },
      { min: 76, max: 94, value: 9 },
      { min: 95, max: 100, value: 11, note: 'the printed row has no 10 column' },
    ],
  },
  4: {
    id: 'escape-q4',
    name: 'Creating Drivers: Escape',
    page: 'p. 4',
    conditionedOn: '# of Qualities = 4',
    bands: [
      { min: 1, max: 14, value: 5 },
      { min: 15, max: 28, value: 6 },
      { min: 29, max: 42, value: 7 },
      { min: 43, max: 56, value: 8 },
      { min: 57, max: 86, value: 10, note: 'the printed row has no 9 column' },
      { min: 87, max: 100, value: 11 },
    ],
  },
  5: {
    id: 'escape-q5',
    name: 'Creating Drivers: Escape',
    page: 'p. 4',
    conditionedOn: '# of Qualities = 5',
    bands: [
      { min: 1, max: 50, value: 7 },
      { min: 51, max: 100, value: 9 },
    ],
  },
};

/** Every table in Part One, for the integrity audit. */
export const ALL_TABLES: RollTable<unknown>[] = [
  NUMBER_OF_QUALITIES,
  QUALITY_BY_DRIVER,
  NUMBER_OF_STARS,
  STAR_QUALITY_OF_DRIVER,
  ...QUALITY_COUNTS.map((q) => GAS_PEDALS[q]),
  ...QUALITY_COUNTS.map((q) => BRAKE_PEDALS[q]),
  ...QUALITY_COUNTS.map((q) => TROPHY_TABLE[q]),
  ...TROPHY_KEYS.map((t) => PIT_CREW[t]),
  ...PIT_CREW_GRADES.map((g) => FAST_PIT[g]),
  ...QUALITY_COUNTS.map((q) => ESCAPE[q]),
] as RollTable<unknown>[];

// ===========================================================================
// D-021 — THE CALIBRATED TABLES
// ===========================================================================
/**
 * D-021, second pass. Re-weighted against the published PLAAY set, measured.
 *
 * GROUND TRUTH: the 106-card **2021 Xfinity Series** set, transcribed in
 * `intake/raw/sample_driver_cards.md` and re-derived by
 * `scripts/analyze-published.ts`. Every band below is that measurement rounded
 * to whole percentage points — nothing here is invented or smoothed to taste.
 *
 * WHAT WAS WRONG. Sagliano's printed tables produce a field whose BACK is
 * nearly as fast as its front:
 *
 *     mean gas pedal        3.32   published 2.65   (25% too fast)
 *     mean gas, no trophy   2.30   published 2.15
 *     mean gas, bronze      3.28   published 2.38   <- the fault
 *     corr(trophy, gas)     0.44   published 0.69
 *
 * A challenge succeeds on `red <= gasPedal` (challenge.ts, p.vi). At 3.28 gas a
 * bronze backmarker clears the red die 55% of the time; at 2.38 he clears it
 * 40%. Across a 43-car field and ~50 turns that difference is the whole
 * difference between a grid that means something and a lottery — before the
 * fix, races were won from 38th, 41st and 35th.
 *
 * THE CHAIN IS UNCHANGED. Same order, same shape, same "each result selects the
 * next table" discipline:
 *
 *   # qualities -> which qualities -> ★ -> GAS -> BRAKE -> TROPHY
 *                                          -> PIT CREW -> FAST PIT -> ESCAPE
 *
 * RULING R-9 — THE TROPHY IS ROLLED AGAINST THE GAS PEDAL, NOT THE QUALITY
 * COUNT. This is the one structural change and it is forced by arithmetic, not
 * preference. Sagliano rolls the trophy on a table chosen by the quality count,
 * which makes trophy and gas conditionally INDEPENDENT given that count. Under
 * that structure
 *
 *     corr(trophy, gas) <= corr(trophy, #qualities) x corr(#qualities, gas)
 *
 * and the published set pins the two right-hand terms at 0.588 and 0.763 —
 * a ceiling of 0.45, which is exactly the 0.44 measured. The published cards
 * carry a residual corr(trophy, gas) of **+0.387 WITHIN a fixed quality count**;
 * no re-weighting of a quality-count-keyed table can reproduce that, because
 * such a table cannot see the gas pedal at all.
 *
 * On the printed cards the gas pedal is simply the better predictor of the
 * trophy than the quality count is (0.694 against 0.588), and the gas pedal is
 * ALREADY ROLLED when the trophy is rolled — it is the immediately preceding
 * link in Sagliano's own published order. So the trophy is re-conditioned onto
 * it. Nothing is re-ordered; one table is re-keyed to the link in front of it,
 * and the quality count still reaches the trophy through the gas pedal it
 * chose. `TROPHY_TABLE` (by quality count) is untouched and still ships under
 * the `sagliano` profile.
 *
 * WHAT THIS BUYS, checked against the published set:
 *   trophy marginal   none 49.3 / bronze 20.0 / silver 19.6 / gold 11.1
 *                     published 49.1 / 19.8 / 19.8 / 11.3
 *   mean gas by trophy  gold 3.83 · silver 3.46 · bronze 2.38 · none 2.15
 *                     published 3.83 · 3.48 · 2.38 · 2.15
 *
 * WHAT WAS *NOT* TOUCHED, and why. `QUALITY_BY_DRIVER`, `NUMBER_OF_STARS`,
 * `STAR_QUALITY_OF_DRIVER` and `PIT_CREW` are shared with the printed profile.
 * Nothing is changed that the measurement does not demand, and two candidates
 * were measured and rejected:
 *
 *   · PIT CREW by trophy. The published rows are non-monotone in a way that
 *     reads as 106-card noise (37% of TROPHYLESS drivers on an A crew, against
 *     33% of gold-trophy ones). Sagliano's rows are clean and, with the
 *     calibrated fast-pit rows, land the fast-pit mean within 0.11 of target
 *     on their own. Left alone.
 *
 *   · NUMBER OF ★. The published mean is 0.434 against ~0.21 here — a real
 *     2x miss, and a calibrated row was written and measured. It made the
 *     field WORSE on every grid statistic (winner's mean start 13.1 -> 13.9,
 *     wins from the top ten 45% -> 40%, distinct winners 15.3 -> 16.8),
 *     because a ★ grants the CHALLENGER a second challenge on that quality
 *     (p.vii) and that outruns the extra brake symbol it gives a DEFENDER who
 *     holds the called quality. The ★ rate is not one of the statistics this
 *     calibration is specified on, so the row was reverted rather than shipped
 *     against the measurement. It stays a known, recorded divergence.
 */

/** The two table sets a driver can be rolled on. */
export type GenProfile = 'sagliano' | 'calibrated';
export const GEN_PROFILES: readonly GenProfile[] = ['sagliano', 'calibrated'];

/** The default for every generated card and every field. */
export const DEFAULT_GEN_PROFILE: GenProfile = 'calibrated';

export const CALIBRATION_SOURCE =
  'PLAAY 2021 Xfinity Series card set, 106 cards (intake/raw/sample_driver_cards.md)';

/** Trophy as an ordinal, for the correlation the calibration is measured on. */
export const TROPHY_RANK: Readonly<Record<TrophyKey, number>> = {
  none: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
};

/**
 * Published: 49 / 26 / 18 / 12 of 106 hold 1 / 2 / 3 / 4 qualities, and one
 * card (Kyle Busch) holds all six. The six-quality card is folded onto the
 * five-quality row — five is the ceiling this model carries, and it is the row
 * that already means "everything".
 *
 * Sagliano's printed row is 12/46/27/12/3, mean 2.48. This is mean 1.96
 * against the published 1.97.
 */
export const CAL_NUMBER_OF_QUALITIES: RollTable<QualityCount> = {
  id: 'cal-number-of-qualities',
  name: 'Number of Qualities (calibrated to the published set)',
  page: 'D-021',
  bands: [
    { min: 1, max: 46, value: 1, note: 'published 46.2%' },
    { min: 47, max: 71, value: 2, note: 'published 24.5%' },
    { min: 72, max: 88, value: 3, note: 'published 17.0%' },
    { min: 89, max: 99, value: 4, note: 'published 11.3%' },
    { min: 100, max: 100, value: 5, note: 'the one six-quality card, folded onto the top row' },
  ],
};

/**
 * GAS PEDALS by quality count — the table the whole fault lived in.
 *
 * Published means: 2.18 / 2.27 / 3.28 / 4.25 / 5.00.
 * Sagliano's:      2.29 / 3.00 / 3.70 / 4.37 / 5.00.
 *
 * The two-quality row is the worst of it — Sagliano puts 78% of those drivers
 * on 3 or 4 gas where the published cards put 100% of them on 2 or 3. Two-
 * quality drivers are a quarter of the field.
 */
export const CAL_GAS_PEDALS: Record<QualityCount, RollTable<number>> = {
  1: {
    id: 'cal-gas-pedals-q1',
    name: 'Gas Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 1',
    bands: [
      { min: 1, max: 84, value: 2 },
      { min: 85, max: 98, value: 3 },
      { min: 99, max: 100, value: 4 },
    ],
  },
  2: {
    id: 'cal-gas-pedals-q2',
    name: 'Gas Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 2',
    bands: [
      { min: 1, max: 73, value: 2 },
      { min: 74, max: 100, value: 3, note: 'no two-quality card in the published set exceeds 3 gas' },
    ],
  },
  3: {
    id: 'cal-gas-pedals-q3',
    name: 'Gas Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 3',
    bands: [
      { min: 1, max: 11, value: 2 },
      { min: 12, max: 67, value: 3 },
      { min: 68, max: 95, value: 4 },
      { min: 96, max: 100, value: 5 },
    ],
  },
  4: {
    id: 'cal-gas-pedals-q4',
    name: 'Gas Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 4',
    bands: [
      { min: 1, max: 17, value: 3 },
      { min: 18, max: 59, value: 4 },
      { min: 60, max: 100, value: 5 },
    ],
  },
  5: {
    id: 'cal-gas-pedals-q5',
    name: 'Gas Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 5',
    bands: [{ min: 1, max: 100, value: 5 }],
  },
};

/**
 * BRAKE PEDALS by quality count.
 *
 * The published set is far more sparing than Sagliano: 83% of the 106 cards
 * carry NO brake at all and the printed mean is 0.24 against Sagliano's 0.77.
 * §2 of the transcription records that in this sample brake is fully determined
 * by gas — 5 gas -> 2 brakes, 4 gas -> 1, 2 or 3 gas -> 0, no exceptions — so
 * the quality-count rows below are near-degenerate at the bottom by
 * construction, not by rounding.
 *
 * NO CARD IN THE PUBLISHED SET CARRIES 3 BRAKES. Sagliano's four-quality row
 * has a 3 column; the printed cards do not, and the calibrated four-quality row
 * therefore tops out at 2.
 */
export const CAL_BRAKE_PEDALS: Record<QualityCount, RollTable<number>> = {
  1: {
    id: 'cal-brake-pedals-q1',
    name: 'Brake Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 1',
    bands: [
      { min: 1, max: 98, value: 0 },
      { min: 99, max: 100, value: 1 },
    ],
  },
  2: {
    id: 'cal-brake-pedals-q2',
    name: 'Brake Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 2',
    bands: [
      { min: 1, max: 100, value: 0, note: 'all 26 published two-quality cards carry no brake' },
    ],
  },
  3: {
    id: 'cal-brake-pedals-q3',
    name: 'Brake Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 3',
    bands: [
      { min: 1, max: 67, value: 0 },
      { min: 68, max: 95, value: 1 },
      { min: 96, max: 100, value: 2 },
    ],
  },
  4: {
    id: 'cal-brake-pedals-q4',
    name: 'Brake Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 4',
    bands: [
      { min: 1, max: 17, value: 0 },
      { min: 18, max: 59, value: 1 },
      { min: 60, max: 100, value: 2 },
    ],
  },
  5: {
    id: 'cal-brake-pedals-q5',
    name: 'Brake Pedals (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 5',
    bands: [{ min: 1, max: 100, value: 2 }],
  },
};

/** The gas-pedal values a trophy can be rolled against. */
export const GAS_PEDAL_VALUES: readonly number[] = [2, 3, 4, 5];

/**
 * RULING R-9 — TROPHY by GAS PEDAL. See the block comment above for why this
 * one table is re-keyed off the quality count.
 *
 * Published, by gas: 2 -> none 71 / bronze 21 / silver 5 / gold 3
 *                    3 -> none 31 / bronze 31 / silver 30 / gold 8
 *                    4 -> silver 64 / gold 36
 *                    5 -> silver 43 / gold 57
 *
 * A 4- or 5-gas car is never trophyless on a printed card, and no 4- or 5-gas
 * car carries bronze — the two facts Sagliano's structure could not express.
 */
export const CAL_TROPHY_BY_GAS: Record<number, RollTable<Trophy>> = {
  2: {
    id: 'cal-trophy-gas2',
    name: 'Trophies or None (calibrated, RULING R-9)',
    page: 'D-021',
    conditionedOn: 'Gas Pedals = 2',
    bands: [
      { min: 1, max: 71, value: null },
      { min: 72, max: 92, value: 'bronze' },
      { min: 93, max: 97, value: 'silver' },
      { min: 98, max: 100, value: 'gold' },
    ],
  },
  3: {
    id: 'cal-trophy-gas3',
    name: 'Trophies or None (calibrated, RULING R-9)',
    page: 'D-021',
    conditionedOn: 'Gas Pedals = 3',
    bands: [
      { min: 1, max: 31, value: null },
      { min: 32, max: 62, value: 'bronze' },
      { min: 63, max: 92, value: 'silver' },
      { min: 93, max: 100, value: 'gold' },
    ],
  },
  4: {
    id: 'cal-trophy-gas4',
    name: 'Trophies or None (calibrated, RULING R-9)',
    page: 'D-021',
    conditionedOn: 'Gas Pedals = 4',
    bands: [
      { min: 1, max: 64, value: 'silver' },
      { min: 65, max: 100, value: 'gold', note: 'no 4-gas card in the published set is trophyless' },
    ],
  },
  5: {
    id: 'cal-trophy-gas5',
    name: 'Trophies or None (calibrated, RULING R-9)',
    page: 'D-021',
    conditionedOn: 'Gas Pedals = 5',
    bands: [
      { min: 1, max: 43, value: 'silver' },
      { min: 44, max: 100, value: 'gold' },
    ],
  },
};

/**
 * FAST PIT by pit crew grade. Sagliano's rows put the printed mean at 4.49
 * once the calibrated trophy mix has moved the grade distribution; the
 * published rows put it at 5.27 against a printed 5.36.
 *
 * Published means by grade: A 7.87 · B 7.87 · C 5.50 · D 4.22 · E 2.58.
 * The union of values still spans 2-10 and still never reaches 11 or 12.
 */
export const CAL_FAST_PIT: Record<PitCrewGrade, RollTable<number>> = {
  A: {
    id: 'cal-fast-pit-a',
    name: 'Fast Pit (calibrated)',
    page: 'D-021',
    conditionedOn: 'Pit Crew = A',
    bands: [
      { min: 1, max: 58, value: 7 },
      { min: 59, max: 67, value: 8 },
      { min: 68, max: 88, value: 9 },
      { min: 89, max: 100, value: 10 },
    ],
  },
  B: {
    id: 'cal-fast-pit-b',
    name: 'Fast Pit (calibrated)',
    page: 'D-021',
    conditionedOn: 'Pit Crew = B',
    bands: [
      { min: 1, max: 20, value: 6 },
      { min: 21, max: 33, value: 7 },
      { min: 34, max: 60, value: 8 },
      { min: 61, max: 100, value: 9 },
    ],
  },
  C: {
    id: 'cal-fast-pit-c',
    name: 'Fast Pit (calibrated)',
    page: 'D-021',
    conditionedOn: 'Pit Crew = C',
    bands: [
      { min: 1, max: 50, value: 5 },
      { min: 51, max: 100, value: 6 },
    ],
  },
  D: {
    id: 'cal-fast-pit-d',
    name: 'Fast Pit (calibrated)',
    page: 'D-021',
    conditionedOn: 'Pit Crew = D',
    bands: [
      { min: 1, max: 14, value: 3 },
      { min: 15, max: 71, value: 4 },
      { min: 72, max: 93, value: 5 },
      { min: 94, max: 100, value: 6 },
    ],
  },
  E: {
    id: 'cal-fast-pit-e',
    name: 'Fast Pit (calibrated)',
    page: 'D-021',
    conditionedOn: 'Pit Crew = E',
    bands: [
      { min: 1, max: 66, value: 2 },
      { min: 67, max: 79, value: 3 },
      { min: 80, max: 97, value: 4 },
      { min: 98, max: 100, value: 5 },
    ],
  },
};

/**
 * ESCAPE by quality count. Sagliano's rows run 2-11 and average 7.0; the
 * published cards run **5-11** and average **8.15**, and no card in the set
 * escapes below 5. The rows below are the published ones — bimodal at 5 and 11,
 * which is what the printed set actually looks like.
 *
 * Conditioning is UNCHANGED: still the quality count, still last in the chain.
 * (The published escape tracks the pit crew grade far more tightly than the
 * quality count — corr 0.88 against 0.10 — but the marginal is what the
 * calibration is specified on, and re-keying a second table on a 106-card
 * sample is more invention than the measurement supports.)
 */
export const CAL_ESCAPE: Record<QualityCount, RollTable<number>> = {
  1: {
    id: 'cal-escape-q1',
    name: 'Escape (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 1',
    bands: [
      { min: 1, max: 39, value: 5 },
      { min: 40, max: 41, value: 6 },
      { min: 42, max: 43, value: 7 },
      { min: 44, max: 51, value: 8 },
      { min: 52, max: 100, value: 11 },
    ],
  },
  2: {
    id: 'cal-escape-q2',
    name: 'Escape (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 2',
    bands: [
      { min: 1, max: 23, value: 5 },
      { min: 24, max: 27, value: 6 },
      { min: 28, max: 46, value: 7 },
      { min: 47, max: 85, value: 8 },
      { min: 86, max: 89, value: 9 },
      { min: 90, max: 100, value: 11 },
    ],
  },
  3: {
    id: 'cal-escape-q3',
    name: 'Escape (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 3',
    bands: [
      { min: 1, max: 6, value: 5 },
      { min: 7, max: 34, value: 7 },
      { min: 35, max: 67, value: 8 },
      { min: 68, max: 78, value: 9 },
      { min: 79, max: 100, value: 11 },
    ],
  },
  4: {
    id: 'cal-escape-q4',
    name: 'Escape (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 4',
    bands: [
      { min: 1, max: 8, value: 5 },
      { min: 9, max: 16, value: 7 },
      { min: 17, max: 33, value: 8 },
      { min: 34, max: 58, value: 9 },
      { min: 59, max: 92, value: 10 },
      { min: 93, max: 100, value: 11 },
    ],
  },
  5: {
    id: 'cal-escape-q5',
    name: 'Escape (calibrated)',
    page: 'D-021',
    conditionedOn: '# of Qualities = 5',
    bands: [{ min: 1, max: 100, value: 11 }],
  },
};

// ---------------------------------------------------------------------------
// The profile bundles
// ---------------------------------------------------------------------------

/** What a trophy roll is keyed on. R-9 is the only place these differ. */
export type TrophyConditioning = 'qualityCount' | 'gasPedal';

/** Every table one profile rolls on, in chain order. */
export interface GenTables {
  profile: GenProfile;
  label: string;
  /** Where this set of numbers came from. Rides in `field_sources`. */
  source: string;
  numberOfQualities: RollTable<QualityCount>;
  qualityByDriver: RollTable<Quality>;
  numberOfStars: RollTable<0 | 1 | 2>;
  starQualityOfDriver: RollTable<Quality>;
  gasPedals: Record<QualityCount, RollTable<number>>;
  brakePedals: Record<QualityCount, RollTable<number>>;
  trophyConditioning: TrophyConditioning;
  trophyByQualityCount: Record<QualityCount, RollTable<Trophy>> | null;
  trophyByGasPedal: Record<number, RollTable<Trophy>> | null;
  pitCrew: Record<TrophyKey, RollTable<PitCrewGrade>>;
  fastPit: Record<PitCrewGrade, RollTable<number>>;
  escape: Record<QualityCount, RollTable<number>>;
}

/** The printed guide, verbatim. Nothing here is re-weighted. */
export const SAGLIANO_TABLES: GenTables = {
  profile: 'sagliano',
  label: 'Sagliano Part One, as printed',
  source: CITE,
  numberOfQualities: NUMBER_OF_QUALITIES,
  qualityByDriver: QUALITY_BY_DRIVER,
  numberOfStars: NUMBER_OF_STARS,
  starQualityOfDriver: STAR_QUALITY_OF_DRIVER,
  gasPedals: GAS_PEDALS,
  brakePedals: BRAKE_PEDALS,
  trophyConditioning: 'qualityCount',
  trophyByQualityCount: TROPHY_TABLE,
  trophyByGasPedal: null,
  pitCrew: PIT_CREW,
  fastPit: FAST_PIT,
  escape: ESCAPE,
};

/** D-021 — the same chain, re-weighted to the published cards. */
export const CALIBRATED_TABLES: GenTables = {
  profile: 'calibrated',
  label: 'D-021, calibrated to the published PLAAY cards',
  source: CALIBRATION_SOURCE,
  numberOfQualities: CAL_NUMBER_OF_QUALITIES,
  // Shared with the printed profile — the measurement does not demand a change.
  qualityByDriver: QUALITY_BY_DRIVER,
  numberOfStars: NUMBER_OF_STARS,
  starQualityOfDriver: STAR_QUALITY_OF_DRIVER,
  gasPedals: CAL_GAS_PEDALS,
  brakePedals: CAL_BRAKE_PEDALS,
  trophyConditioning: 'gasPedal',
  trophyByQualityCount: null,
  trophyByGasPedal: CAL_TROPHY_BY_GAS,
  pitCrew: PIT_CREW,
  fastPit: CAL_FAST_PIT,
  escape: CAL_ESCAPE,
};

export const TABLES_BY_PROFILE: Readonly<Record<GenProfile, GenTables>> = {
  sagliano: SAGLIANO_TABLES,
  calibrated: CALIBRATED_TABLES,
};

/** The trophy table this profile rolls on, given what the chain has produced. */
export function trophyTableFor(
  tables: GenTables,
  qualityCount: QualityCount,
  gasPedal: number,
): RollTable<Trophy> {
  if (tables.trophyConditioning === 'qualityCount') {
    const t = tables.trophyByQualityCount?.[qualityCount];
    if (!t) throw new Error(`${tables.profile}: no trophy table for quality count ${qualityCount}`);
    return t;
  }
  const t = tables.trophyByGasPedal?.[gasPedal];
  if (!t) {
    throw new Error(
      `${tables.profile}: no trophy table for ${gasPedal} gas pedals. RULING R-9 keys the trophy ` +
        `on the gas pedal, so every value the gas tables can produce needs a row.`,
    );
  }
  return t;
}

/** Every calibrated table, for the integrity audit. */
export const ALL_CALIBRATED_TABLES: RollTable<unknown>[] = [
  CAL_NUMBER_OF_QUALITIES,
  ...QUALITY_COUNTS.map((q) => CAL_GAS_PEDALS[q]),
  ...QUALITY_COUNTS.map((q) => CAL_BRAKE_PEDALS[q]),
  ...GAS_PEDAL_VALUES.map((g) => CAL_TROPHY_BY_GAS[g]),
  ...PIT_CREW_GRADES.map((g) => CAL_FAST_PIT[g]),
  ...QUALITY_COUNTS.map((q) => CAL_ESCAPE[q]),
] as RollTable<unknown>[];

// ---------------------------------------------------------------------------
// Integrity check — run over any roll table before trusting it
// ---------------------------------------------------------------------------

export interface TableAudit {
  ok: boolean;
  problems: string[];
}

/**
 * Verify a roll table partitions 1-100 with no gaps, no overlaps and no
 * inverted bounds — the same discipline `auditBandTable` applies to the
 * re-rating band tables in rerate.ts, moved to the 1-100 roll domain.
 *
 * A gap here is not cosmetic: it is a roll the generator cannot resolve, which
 * would surface as a thrown error weeks later in the middle of a season.
 */
export function auditRollTable<T>(table: RollTable<T>): TableAudit {
  const problems: string[] = [];
  const bands = [...table.bands].sort((a, b) => a.min - b.min);

  if (bands.length === 0) return { ok: false, problems: [`${table.id}: table has no bands`] };

  for (const b of bands) {
    if (!Number.isInteger(b.min) || !Number.isInteger(b.max)) {
      problems.push(`${table.id}: non-integer band ${b.min}-${b.max}`);
    }
    if (b.min > b.max) problems.push(`${table.id}: inverted band ${b.min}-${b.max}`);
    if (b.min < 1 || b.max > 100) problems.push(`${table.id}: band ${b.min}-${b.max} escapes 1-100`);
  }
  if (bands[0].min !== 1) problems.push(`${table.id}: starts at ${bands[0].min}, not 1`);
  const last = bands[bands.length - 1];
  if (last.max !== 100) problems.push(`${table.id}: ends at ${last.max}, not 100`);

  for (let i = 1; i < bands.length; i++) {
    const prev = bands[i - 1];
    const cur = bands[i];
    if (cur.min > prev.max + 1) {
      problems.push(`${table.id}: GAP — nothing covers ${prev.max + 1}-${cur.min - 1}`);
    } else if (cur.min <= prev.max) {
      problems.push(`${table.id}: OVERLAP — ${cur.min}-${prev.max} is in two bands`);
    }
  }

  const seen = new Set<T>();
  for (const b of bands) {
    if (seen.has(b.value)) problems.push(`${table.id}: value ${String(b.value)} appears twice`);
    seen.add(b.value);
  }

  return { ok: problems.length === 0, problems };
}

/**
 * Audit every table the generator can roll on — printed AND calibrated.
 * Returns only the tables with problems.
 */
export function auditAllTables(): { table: string; problems: string[] }[] {
  return [...ALL_TABLES, ...ALL_CALIBRATED_TABLES]
    .map((t) => ({ table: t.id, problems: auditRollTable(t).problems }))
    .filter((r) => r.problems.length > 0);
}

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/** One roll, with everything needed to re-check it by hand against the guide. */
export interface RollRecord {
  /** 1-based position in the generation order. */
  step: number;
  /** Card field this roll fed: 'qualityCount', 'quality', 'gasPedal', ... */
  field: string;
  tableId: string;
  /** The table's printed name. */
  table: string;
  page: string;
  /** The earlier result that selected this table, or null for an entry point. */
  conditionedOn: string | null;
  /** 1-100. */
  roll: number;
  /** The two d10 faces as thrown. */
  dice: [number, number];
  /** The band the roll landed in, e.g. "59-85". */
  band: string;
  /** The band's value, rendered. */
  result: string;
  /** False when the roll was discarded (a duplicate quality, an unheld star). */
  accepted: boolean;
  /** Why it was discarded, or a transcription note carried by the band. */
  note?: string;
}

/** A field the card carries that no Sagliano table produces. */
export interface UnrolledField {
  field: string;
  value: string;
  ruling: string;
}

/**
 * D-021/D-024 — where one value on the card came from.
 *
 * `printed` is reserved for a real mailed card superseding a generated one;
 * nothing in this module ever emits it. Everything the generator produces is
 * marked `sagliano`, `calibrated` or `ruling`, so a calibrated roll is always
 * distinguishable from a printed value.
 */
export type FieldSourceKind = 'printed' | 'sagliano' | 'calibrated' | 'ruling';

export interface FieldSource {
  /** Card field: 'gasPedal', 'trophy', 'triangle', ... */
  field: string;
  kind: FieldSourceKind;
  /** The table that produced it, or null for a ruling. */
  tableId: string | null;
  /** The set of numbers behind it — a cite, or the calibration's ground truth. */
  source: string;
}

export interface DriverProvenance {
  source: string;
  /** Which table set rolled this card. */
  profile: GenProfile;
  /**
   * D-021 — one entry per card field, marking generated values as calibrated
   * or as-printed so a real card can later supersede a generated one.
   */
  fieldSources: FieldSource[];
  /** Every roll in order, discards included. This is the audit trail. */
  log: RollRecord[];
  qualityCount: RollRecord;
  qualities: RollRecord[];
  starCount: RollRecord;
  stars: RollRecord[];
  gasPedal: RollRecord;
  brakePedal: RollRecord;
  trophy: RollRecord;
  pitCrew: RollRecord;
  fastPit: RollRecord;
  escape: RollRecord;
  /** Rulings that actually bit while generating this driver. */
  rulings: string[];
  /** Card fields set without a roll, each with the ruling that set it. */
  unrolled: UnrolledField[];
}

export interface GeneratedDriver {
  card: DriverCard;
  provenance: DriverProvenance;
}

/**
 * Where a driver's NAME came from. Not a card field — a provenance label.
 *
 * `vocabulary` is the built-in FIRST_NAMES × SURNAMES cross product in
 * `field.ts`: a fixed, hand-curated list that produces the same names for the
 * same seed forever. `pool` is a batch of names generated once by the model and
 * persisted (`src/lib/nameStore.ts`).
 *
 * HOWARD, 17 Aug 2026, is the whole reason this distinction is recorded:
 *   "are the fictional names the same every time? I dont like that. Let the ai
 *    make a batch first and then changing the seed only moves around those AI
 *    created names and doesnt remake more."
 *
 * Two vocabularies means a card can now be named two different ways, and this
 * project does not let a value on a card be unexplainable. A pool-named driver
 * carries the label so `explainDriver` can say which list named him.
 */
export type NameOrigin = 'vocabulary' | 'pool';

/**
 * The ruling recorded against a name dealt from the AI pool.
 *
 * No Sagliano table produces a driver name — his chain rolls ratings and
 * nothing else — so a name has always been an unrolled field. It was not
 * previously DECLARED as one because there was only ever one source for it.
 * There are now two, so it is declared, exactly like `triangle`, `fastPitDouble`
 * and `age` above.
 */
export const NAME_POOL_RULING =
  'R-NP1: dealt from the AI-generated name pool, not from the built-in first-name × ' +
  'surname vocabulary. No published table produces a driver name (Sagliano rolls ' +
  'ratings only), so the source of the name is recorded rather than assumed. The pool ' +
  'is generated once, on a first visit with no career running, and re-seeding only ' +
  'reshuffles it — it never generates more.';

export interface DriverGenOptions {
  /** Must be unique across a field. */
  driverId?: string;
  name?: string;
  /**
   * Which vocabulary supplied `name`. Default: no label at all.
   *
   * ⚠ THE DEFAULT IS DELIBERATELY *ABSENT*, NOT `'vocabulary'`. Adding an
   * unconditional fourth entry to `provenance.unrolled` would break
   * `driverGen.test.ts`'s `toStrictEqual(['triangle', 'fastPitDouble', 'age'])`
   * and its line count for `explainDriver` — and those tests are pinning real
   * behaviour, not an accident. A card named the way every card has always been
   * named therefore looks exactly as it did before this option existed; only a
   * card named from the pool gains a line.
   */
  nameOrigin?: NameOrigin;
  /** A string: the published sets contain `0`, `07` and a bare `X`. */
  carNumber?: string;
  teamId?: string;
  /**
   * Which table set to roll on. Default `calibrated` (D-021). Pass
   * `'sagliano'` for the printed guide's own numbers, unchanged.
   */
  profile?: GenProfile;
}

/**
 * Re-roll guard. Both re-roll loops (duplicate qualities, stars landing on
 * unheld qualities) terminate with probability 1 on fair dice; this bound only
 * exists so a mis-scripted or degenerate die source fails loudly instead of
 * hanging the season.
 */
const MAX_REROLLS = 500;

// ---------------------------------------------------------------------------
// The generator
// ---------------------------------------------------------------------------

/**
 * Create one inaugural-season driver, rolling Sagliano's chain in order.
 *
 * The order is load-bearing and is asserted by the tests: quality count picks
 * the gas, brake, trophy and escape tables; the trophy picks the pit crew
 * table; the pit crew grade picks the fast pit table.
 */
export function createDriver(dice: Dice, options: DriverGenOptions = {}): GeneratedDriver {
  const profile = options.profile ?? DEFAULT_GEN_PROFILE;
  const tables = TABLES_BY_PROFILE[profile];
  if (!tables) throw new RangeError(`unknown driver-generation profile "${profile}"`);

  const log: RollRecord[] = [];
  const rulings: string[] = [];

  const roll = <T>(
    field: string,
    table: RollTable<T>,
    accepted = true,
    note?: string,
  ): { record: RollRecord; value: T } => {
    const p = rollPercentile(dice);
    const band = lookup(table, p.value);
    const carried = note ?? band.note;
    const record: RollRecord = {
      step: log.length + 1,
      field,
      tableId: table.id,
      table: table.name,
      page: table.page,
      conditionedOn: table.conditionedOn ?? null,
      roll: p.value,
      dice: p.dice,
      band: bandLabel(band),
      result: band.value === null ? 'NONE' : String(band.value),
      accepted,
      ...(carried !== undefined ? { note: carried } : {}),
    };
    log.push(record);
    return { record, value: band.value };
  };

  // --- 1. How many qualities. Everything downstream hangs off this. ---------
  const countRoll = roll('qualityCount', tables.numberOfQualities);
  const qualityCount = countRoll.value;

  // --- 2. Which qualities. "Ignore the same quality rolled repeatedly." -----
  const qualities: Quality[] = [];
  const qualityRecords: RollRecord[] = [];
  for (let guard = 0; qualities.length < qualityCount; guard++) {
    if (guard > MAX_REROLLS) {
      throw new Error(`driver creation: ${MAX_REROLLS} rolls without filling ${qualityCount} qualities`);
    }
    const r = roll('quality', tables.qualityByDriver, true);
    if (qualities.includes(r.value)) {
      r.record.accepted = false;
      r.record.note = 'duplicate — "Ignore the same quality rolled repeatedly" (p. 1)';
      continue;
    }
    qualities.push(r.value);
    qualityRecords.push(r.record);
  }

  // --- 3. How many ★. ------------------------------------------------------
  const starCountRoll = roll('starCount', tables.numberOfStars);
  let starTarget: number = starCountRoll.value;

  // RULING R-3 — cap the ★ count at the qualities actually held. A star marks
  // an existing quality (the base game's star grants a SECOND challenge on that
  // quality, and the post-race interview chart's "STAR A QUALITY" / "GAIN HEAT
  // — if Racer already has HEAT, quality is now Starred" both treat starring as
  // an upgrade to something already on the card). A one-quality driver who
  // rolls two stars therefore takes one; there is nothing for the second to
  // mark. Sagliano never states the case because his ★ count and quality count
  // are rolled from separate tables that can disagree.
  if (starTarget > qualities.length) {
    rulings.push(
      `R-3: ★ count ${starTarget} capped to ${qualities.length} — a star marks a quality the driver holds.`,
    );
    starTarget = qualities.length;
  }

  // --- 4. Which quality is ★. ---------------------------------------------
  const starred: Quality[] = [];
  const starRecords: RollRecord[] = [];
  for (let i = 0; i < starTarget; i++) {
    let placed = false;
    for (let guard = 0; !placed; guard++) {
      if (guard > MAX_REROLLS) {
        throw new Error(`driver creation: ${MAX_REROLLS} rolls without placing ★ ${i + 1}`);
      }
      const r = roll('star', tables.starQualityOfDriver, true);

      // RULING R-2 / R-4 — discard and re-roll a star that lands on a quality
      // the driver does not hold, or on one already starred. Sagliano's own
      // idiom for an inapplicable roll is to re-roll it: "Ignore the same
      // quality rolled repeatedly" (p. 1) and "Re-roll any STAR and HEAT rolls"
      // (p. 7). Treating the star as wasted instead would silently drop the
      // published ~21% star rate to near zero for one- and two-quality drivers.
      if (!qualities.includes(r.value)) {
        r.record.accepted = false;
        r.record.note = `RULING R-2: driver does not hold ${r.value} — re-roll`;
        if (!rulings.some((x) => x.startsWith('R-2'))) {
          rulings.push('R-2: a ★ rolled on a quality the driver does not hold is re-rolled.');
        }
        continue;
      }
      if (starred.includes(r.value)) {
        r.record.accepted = false;
        r.record.note = `RULING R-4: ${r.value} is already starred — re-roll`;
        if (!rulings.some((x) => x.startsWith('R-4'))) {
          rulings.push('R-4: the same quality is never starred twice.');
        }
        continue;
      }
      starred.push(r.value);
      starRecords.push(r.record);
      placed = true;
    }
  }

  // --- 5. Gas Pedals — table chosen by the quality count. -------------------
  const gas = roll('gasPedal', tables.gasPedals[qualityCount]);

  // --- 6. Brake Pedals — table chosen by the quality count. -----------------
  const brake = roll('brakePedal', tables.brakePedals[qualityCount]);

  // --- 7. Trophy. May be NONE. ---------------------------------------------
  // `sagliano`   — table chosen by the quality count, as printed.
  // `calibrated` — RULING R-9: table chosen by the GAS PEDAL just rolled. The
  //                order is unchanged; only the key on this one table moves,
  //                because a quality-count-keyed table cannot see the gas
  //                pedal and so cannot reproduce the published set's +0.387
  //                residual corr(trophy, gas) within a fixed quality count.
  const trophy = roll('trophy', trophyTableFor(tables, qualityCount, gas.value));
  if (tables.trophyConditioning === 'gasPedal') {
    rulings.push(
      `R-9: the trophy was rolled on the ${gas.value}-gas row, not the ` +
        `${qualityCount}-quality row — D-021 keys the trophy on the gas pedal it follows.`,
    );
  }

  // --- 8. Pit Crew — table chosen by the TROPHY, not the quality count. -----
  const pitCrew = roll('pitCrewGrade', tables.pitCrew[trophyKeyOf(trophy.value)]);

  // --- 9. Fast Pit — table chosen by the PIT CREW grade. --------------------
  const fastPit = roll('fastPit', tables.fastPit[pitCrew.value]);

  // --- 10. Escape — table chosen by the quality count. ---------------------
  const escape = roll('escape', tables.escape[qualityCount]);

  // Fields no Sagliano table produces. Recorded rather than quietly defaulted.
  const unrolled: UnrolledField[] = [
    {
      field: 'triangle',
      value: 'false',
      ruling:
        'R-6: Sagliano publishes no symbol table. The red ▲ appears on 14 of 106 sample cards but ' +
        'is unlabelled and unmodelled here; creation never sets it.',
    },
    {
      field: 'fastPitDouble',
      value: 'null',
      ruling:
        'R-7: the split fast-pit grade is an EZRA add-on (FatPit_DoubleFastPit), not part of ' +
        "Sagliano's Part One. Left null so no double fast pit is implied.",
    },
    {
      field: 'age',
      value: 'unset',
      ruling: "R-8: Sagliano's model has no age field. Age is an explicit house layer added elsewhere.",
    },
  ];

  // R-NP1. Only when the caller says the name came from the pool — see the
  // warning on `DriverGenOptions.nameOrigin` for why this is not unconditional.
  // It is pushed HERE, before `fieldSources` is built below, because that array
  // maps over `unrolled` to mark every unrolled field as ruling-sourced.
  if (options.nameOrigin === 'pool') {
    unrolled.push({
      field: 'name',
      value: options.name ?? 'Unnamed Driver',
      ruling: NAME_POOL_RULING,
    });
  }

  const card: DriverCard = {
    driverId: options.driverId ?? 'unassigned',
    name: options.name ?? 'Unnamed Driver',
    // 'X' is a published car number in its own right — 6 of the 106 sample
    // cards carry a bare X with no digits.
    carNumber: options.carNumber ?? 'X',
    gasPedal: gas.value,
    brakePedal: brake.value,
    qualities: sortToCardOrder(qualities),
    starred: sortToCardOrder(starred),
    escape: escape.value,
    fastPit: fastPit.value,
    fastPitDouble: null, // RULING R-7
    trophy: trophy.value,
    pitCrewGrade: pitCrew.value,
    triangle: false, // RULING R-6
    ...(options.teamId !== undefined ? { teamId: options.teamId } : {}),
  };

  // D-021 — one line per card field saying which set of numbers produced it, so
  // a mailed printed card can later supersede a generated one without guesswork.
  const rolledKind: FieldSourceKind = profile === 'calibrated' ? 'calibrated' : 'sagliano';
  const rolled = (field: string, record: RollRecord): FieldSource => ({
    field,
    kind: rolledKind,
    tableId: record.tableId,
    source: tables.source,
  });
  const fieldSources: FieldSource[] = [
    rolled('qualities', countRoll.record),
    rolled('starred', starCountRoll.record),
    rolled('gasPedal', gas.record),
    rolled('brakePedal', brake.record),
    rolled('trophy', trophy.record),
    rolled('pitCrewGrade', pitCrew.record),
    rolled('fastPit', fastPit.record),
    rolled('escape', escape.record),
    ...unrolled.map(
      (u): FieldSource => ({ field: u.field, kind: 'ruling', tableId: null, source: u.ruling }),
    ),
  ];

  return {
    card,
    provenance: {
      source: tables.source,
      profile,
      fieldSources,
      log,
      qualityCount: countRoll.record,
      qualities: qualityRecords,
      starCount: starCountRoll.record,
      stars: starRecords,
      gasPedal: gas.record,
      brakePedal: brake.record,
      trophy: trophy.record,
      pitCrew: pitCrew.record,
      fastPit: fastPit.record,
      escape: escape.record,
      rulings,
      unrolled,
    },
  };
}

/**
 * Qualities in the order the printed card grid uses (REFLEX·CONTROL·SKILL /
 * STAR·SMART·HEAT), not roll order. Roll order is preserved in the provenance
 * log; the card reads the way a card reads.
 */
function sortToCardOrder(qualities: Quality[]): Quality[] {
  return QUALITIES.filter((q) => qualities.includes(q));
}

// ---------------------------------------------------------------------------
// The name-pool seam
// ---------------------------------------------------------------------------

/**
 * What `applyNamePool` did, counted. Nothing here is inferred — every number is
 * a count of something that happened.
 */
export interface NamePoolApplication<T extends GeneratedDriver> {
  /** The same drivers, in the same order, with pool names where one was available. */
  drivers: T[];
  /** How many cards took a name from the pool. */
  renamed: number;
  /**
   * How many cards kept the name the built-in vocabulary already gave them,
   * because the pool ran out or offered nothing usable for that slot.
   *
   * THIS IS THE HONEST-FALLBACK NUMBER and the caller is expected to surface
   * it. A pool that covers 40 of 53 cards must not be reported as "AI names";
   * it is "40 AI names and 13 from the built-in list", which is a true sentence.
   */
  keptBuiltIn: number;
  /** The names that were kept, so the caller can name them rather than count them. */
  keptNames: string[];
}

/**
 * Rename an already-built field from an externally supplied pool of full names.
 *
 * WHY THIS IS A POST-PASS AND NOT A `buildField` OPTION.
 * `buildField` already takes `firstNames` / `surnames` and combines them into a
 * cross product. Feeding an AI pool through that seam would split "Otis Barkley"
 * and "Chase Coffey" into parts and hand back "Otis Coffey" — a name the model
 * never wrote. Howard's requirement is explicit that re-seeding "only moves
 * around those AI created names and doesnt remake more", and recombining halves
 * IS remaking more. So pool names travel WHOLE, and the only way to keep them
 * whole is to assign them after the cross product has already done its job.
 *
 * The upside of the post-pass is that the ratings are untouched: `buildField`
 * rolls names off a separate RNG stream from the cards precisely so that
 * changing the name pools never moves a rating, and renaming afterwards cannot
 * move one either. A field renamed from the pool has exactly the same cards as
 * the same field unrenamed.
 *
 * PERMUTATION IS NOT THIS FUNCTION'S JOB. It consumes `names` in the order
 * given. `src/lib/nameStore.ts` decides which names, in which order, for a given
 * seed — this is the substitution and the provenance, nothing else.
 *
 * @param drivers the field, in order. Not mutated.
 * @param names   pool names, already permuted for this seed. May be shorter than
 *                `drivers`; the tail keeps its built-in name.
 */
export function applyNamePool<T extends GeneratedDriver>(
  drivers: readonly T[],
  names: readonly string[],
): NamePoolApplication<T> {
  // The pool is validated in `nameStore.ts` before it is ever persisted, but a
  // domain function does not get to assume its caller did its job — this is the
  // same reason `lookup` re-checks a roll it was handed. Blanks and duplicates
  // are skipped rather than thrown on: a bad name must not be able to take the
  // league down, and a card with its built-in name is a working card.
  const used = new Set<string>(drivers.map((d) => d.card.name));
  const queue: string[] = [];
  const seen = new Set<string>();
  for (const raw of names) {
    if (typeof raw !== 'string') continue;
    const name = raw.trim();
    if (name.length === 0) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    queue.push(name);
  }

  let cursor = 0;
  let renamed = 0;
  const keptNames: string[] = [];

  const out = drivers.map((driver) => {
    // A pool name identical to a built-in name already dealt in this field
    // would put two identical drivers on the grid, which every downstream
    // consumer (the booth's name gate above all) treats as one driver.
    while (cursor < queue.length && used.has(queue[cursor]) && queue[cursor] !== driver.card.name) {
      cursor++;
    }
    if (cursor >= queue.length) {
      keptNames.push(driver.card.name);
      return driver;
    }
    const name = queue[cursor++];
    used.delete(driver.card.name);
    used.add(name);
    renamed++;
    return {
      ...driver,
      card: { ...driver.card, name },
      provenance: {
        ...driver.provenance,
        unrolled: [
          ...driver.provenance.unrolled,
          { field: 'name', value: name, ruling: NAME_POOL_RULING },
        ],
        fieldSources: [
          ...driver.provenance.fieldSources,
          { field: 'name', kind: 'ruling' as const, tableId: null, source: NAME_POOL_RULING },
        ],
      },
    };
  });

  return { drivers: out, renamed, keptBuiltIn: keptNames.length, keptNames };
}

// ---------------------------------------------------------------------------
// Explaining a driver
// ---------------------------------------------------------------------------

/**
 * The whole chain in plain language, one line per roll — for an audit panel,
 * the booth, or a bug report. Every published table is named and cited, so a
 * disputed driver can be checked against the printed page in a minute.
 */
export function explainDriver(driver: GeneratedDriver): string[] {
  const { card, provenance } = driver;
  const lines: string[] = [
    `${card.name} (#${card.carNumber}) — created on ${provenance.source}`,
  ];

  for (const r of provenance.log) {
    const where = r.conditionedOn ? `${r.table} [${r.conditionedOn}]` : r.table;
    const verdict = r.accepted ? `→ ${r.result}` : `→ ${r.result} (discarded)`;
    const note = r.note ? `  — ${r.note}` : '';
    lines.push(
      `  ${String(r.step).padStart(2)}. ${r.field}: rolled ${r.roll} ` +
        `(d10 ${r.dice[0]}/${r.dice[1]}) on ${where} ${r.page}, band ${r.band} ${verdict}${note}`,
    );
  }

  lines.push(
    `  = ${card.gasPedal} gas / ${card.brakePedal} brake · ` +
      `${card.qualities.join(', ') || 'no qualities'}` +
      (card.starred.length ? ` (★ ${card.starred.join(', ')})` : '') +
      ` · trophy ${card.trophy ?? 'NONE'} · pit crew ${card.pitCrewGrade} · ` +
      `fast pit ${card.fastPit} · escape ${card.escape}`,
  );
  for (const u of provenance.unrolled) lines.push(`  · ${u.field} = ${u.value} — ${u.ruling}`);

  return lines;
}
